/**
 * DocumentImportWizard Component
 * Multi-step wizard for importing documents with account resolution and transaction review
 *
 * Step Validation (WP-BUG-13):
 * - Account resolution step must be completed before transaction review
 * - The backend requires confirmed_account_id before import will succeed
 *
 * ID Type Handling (WP-BUG-19):
 * - ?id={stagedImportId} - Direct access to existing staged import
 * - ?documentId={documentId} - Document is processing, wizard polls until staged import ready
 */
import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { useStagedImport, useStagedImportByDocumentId, useRetryDocument } from '@/hooks/useDocuments';
import UploadStep from './UploadStep';
import AccountResolutionStep from './AccountResolutionStep';
import TransactionReviewStep from './TransactionReviewStep';
import type { WizardStep, StagedImport, DocumentStatus } from '@/types/document';

/**
 * Check if the user can proceed to a given step based on staged import state
 * This enforces the proper wizard flow (WP-BUG-13)
 */
function canProceedToStep(step: WizardStep, stagedImport: StagedImport | null | undefined): boolean {
  if (!stagedImport) {
    return step === 'upload';
  }

  switch (step) {
    case 'upload':
      return true;
    case 'account_resolution':
      return true;
    case 'transaction_review':
      // Can only proceed to transaction review if account is confirmed
      return Boolean(stagedImport.confirmedAccountId);
    case 'complete':
      return Boolean(stagedImport.confirmedAccountId);
    default:
      return false;
  }
}

/**
 * Get the appropriate step based on URL param and staged import state
 * Falls back to earlier step if requirements aren't met (WP-BUG-13)
 */
function getValidatedStep(
  requestedStep: string | null,
  stagedImportId: string | null,
  stagedImport: StagedImport | null | undefined
): WizardStep {
  // If no staged import ID, start at upload
  if (!stagedImportId) {
    return 'upload';
  }

  // Default to account_resolution if no step specified
  if (!requestedStep || requestedStep === 'upload') {
    return 'account_resolution';
  }

  // Validate the requested step
  if (requestedStep === 'transaction_review' || requestedStep === 'complete') {
    if (stagedImport && canProceedToStep(requestedStep, stagedImport)) {
      return requestedStep;
    }
    // Fall back to account_resolution if requirements not met
    return 'account_resolution';
  }

  if (requestedStep === 'account_resolution') {
    return requestedStep;
  }

  return 'account_resolution';
}

// Step configuration
const STEPS: { key: WizardStep; label: string; number: number }[] = [
  { key: 'upload', label: 'Upload', number: 1 },
  { key: 'account_resolution', label: 'Match Accounts', number: 2 },
  { key: 'transaction_review', label: 'Review Transactions', number: 3 },
  { key: 'complete', label: 'Complete', number: 4 },
];

/**
 * Get the step number from the step key
 */
function getStepNumber(step: WizardStep): number {
  return STEPS.find((s) => s.key === step)?.number ?? 1;
}

/**
 * Get a user-friendly message for document processing status (WP-BUG-19)
 */
function getProcessingStatusMessage(status: DocumentStatus | null): string | null {
  if (!status) return null;

  switch (status) {
    case 'ingested':
    case 'uploading':
      return 'Preparing document for analysis...';
    case 'classifying':
      return 'Identifying document type...';
    case 'classified':
    case 'parsing':
      return 'Extracting transaction data...';
    case 'parsed':
    case 'staged':
      return 'Almost ready...';
    case 'processing':
      return 'Analyzing your document...';
    default:
      return null;
  }
}

export default function DocumentImportWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // WP-BUG-19: Handle both URL parameter types
  // - ?id={stagedImportId} - Direct access to existing staged import
  // - ?documentId={documentId} - Document is processing, need to poll
  const stagedImportIdParam = searchParams.get('id');
  const documentIdParam = searchParams.get('documentId');

  // Track current wizard step - initial value based on URL
  const [currentStep, setCurrentStep] = useState<WizardStep>(() => {
    const step = searchParams.get('step');
    if (step === 'account_resolution' || step === 'transaction_review' || step === 'complete') {
      return step;
    }
    // Default to account resolution if we have an ID
    return (stagedImportIdParam || documentIdParam) ? 'account_resolution' : 'upload';
  });

  // Fetch staged import directly when we have a staged import ID
  const directStagedImportQuery = useStagedImport(stagedImportIdParam ?? '');

  // WP-BUG-19: Poll for staged import when we only have a document ID
  const documentPollingResult = useStagedImportByDocumentId(
    documentIdParam && !stagedImportIdParam ? documentIdParam : null
  );

  // Determine which data source to use
  const isUsingDocumentPolling = Boolean(documentIdParam && !stagedImportIdParam);
  const effectiveStagedImportId = stagedImportIdParam ?? documentPollingResult.stagedImportId;
  const stagedImport = isUsingDocumentPolling
    ? documentPollingResult.stagedImport
    : directStagedImportQuery.data;
  const isLoading = isUsingDocumentPolling
    ? documentPollingResult.isProcessing
    : directStagedImportQuery.isLoading;
  const error = isUsingDocumentPolling
    ? documentPollingResult.error
    : directStagedImportQuery.error;

  // WP-BUG-19: Once we have a staged import ID from polling, update the URL
  useEffect(() => {
    if (isUsingDocumentPolling && documentPollingResult.stagedImportId) {
      // Replace documentId with id in URL for cleaner state
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('documentId');
      newParams.set('id', documentPollingResult.stagedImportId);
      setSearchParams(newParams, { replace: true });
    }
  }, [isUsingDocumentPolling, documentPollingResult.stagedImportId, searchParams, setSearchParams]);

  // Validate step after staged import loads (WP-BUG-13)
  // This ensures users cannot stay on transaction_review step without a confirmed account
  useEffect(() => {
    if (!isLoading && stagedImport) {
      const requestedStep = searchParams.get('step');
      const validatedStep = getValidatedStep(requestedStep, effectiveStagedImportId, stagedImport);

      if (validatedStep !== currentStep) {
        // Step was invalid - redirect to correct step
        setCurrentStep(validatedStep);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('step', validatedStep);
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [isLoading, stagedImport, effectiveStagedImportId, searchParams, currentStep, setSearchParams]);

  // Update URL when step changes
  const updateStep = useCallback(
    (step: WizardStep) => {
      // Validate before allowing navigation (WP-BUG-13)
      if (!canProceedToStep(step, stagedImport)) {
        // Cannot proceed to this step - redirect to account_resolution
        setCurrentStep('account_resolution');
        const newParams = new URLSearchParams(searchParams);
        newParams.set('step', 'account_resolution');
        setSearchParams(newParams, { replace: true });
        return;
      }

      setCurrentStep(step);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('step', step);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, stagedImport]
  );

  // Handle step navigation
  const handleAccountResolutionComplete = useCallback(() => {
    updateStep('transaction_review');
  }, [updateStep]);

  const handleTransactionReviewComplete = useCallback(() => {
    updateStep('complete');
  }, [updateStep]);

  const handleBack = useCallback(() => {
    if (currentStep === 'transaction_review') {
      updateStep('account_resolution');
    } else if (currentStep === 'complete') {
      updateStep('transaction_review');
    }
  }, [currentStep, updateStep]);

  const handleFinish = useCallback(() => {
    navigate('/transactions');
  }, [navigate]);

  const handleCancel = useCallback(() => {
    navigate('/accounts');
  }, [navigate]);

  /**
   * Handle upload step completion (WP-BUG-19)
   * The result string is prefixed with either "id:" or "documentId:" to indicate type
   */
  const handleUploadComplete = useCallback(
    (result: string) => {
      const newParams = new URLSearchParams(searchParams);

      if (result.startsWith('id:')) {
        // Staged import is ready immediately
        newParams.set('id', result.substring(3));
      } else if (result.startsWith('documentId:')) {
        // Document is still processing - use documentId param for polling
        newParams.set('documentId', result.substring(11));
      } else {
        // Fallback for legacy behavior - treat as staged import ID
        newParams.set('id', result);
      }

      newParams.set('step', 'account_resolution');
      setSearchParams(newParams);
      setCurrentStep('account_resolution');
    },
    [searchParams, setSearchParams]
  );

  // Show upload step when no ID of either type
  if (!stagedImportIdParam && !documentIdParam) {
    return (
      <div className="max-w-4xl mx-auto">
        <WizardHeader currentStep="upload" stagedImport={null} />
        <p className="mb-4 text-sm text-gray-600">
          Moving from Mint, YNAB or Quicken?{' '}
          <Link to="/import/apps" className="font-medium text-primary-600 hover:text-primary-500">
            Import from another app
          </Link>
        </p>
        <UploadStep
          onComplete={handleUploadComplete}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  // WP-BUG-19: Show processing state when polling for document
  if (isLoading) {
    const isDocumentProcessing = isUsingDocumentPolling && documentPollingResult.isProcessing;
    const processingMessage = isDocumentProcessing
      ? 'Processing your document...'
      : 'Loading import data...';
    const processingSubMessage = isDocumentProcessing
      ? getProcessingStatusMessage(documentPollingResult.documentStatus)
      : null;

    return (
      <div className="max-w-4xl mx-auto">
        <WizardHeader currentStep={currentStep} stagedImport={null} />
        <Card>
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner />
            <span className="mt-3 text-gray-900 font-medium">{processingMessage}</span>
            {processingSubMessage && (
              <span className="mt-1 text-sm text-gray-500">{processingSubMessage}</span>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // WP-BUG-19: Handle document processing failure
  if (isUsingDocumentPolling && documentPollingResult.documentStatus &&
      (documentPollingResult.documentStatus === 'failed' || documentPollingResult.documentStatus === 'error')) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <ErrorIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Document Processing Failed</h3>
            <p className="mt-2 text-sm text-gray-500">
              {documentPollingResult.documentError || 'There was a problem processing your document. Please try uploading again.'}
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => navigate('/documents/import')}>
                Try Again
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Handle orphaned documents: document reached terminal status but no staged import created
  // This can happen when documents were processed before a bug fix or only contain balance info
  if (isUsingDocumentPolling && documentPollingResult.isOrphanedDocument) {
    return (
      <OrphanedDocumentError
        documentId={documentIdParam!}
        documentStatus={documentPollingResult.documentStatus}
        onCancel={handleCancel}
        onRetry={() => navigate('/documents/import')}
      />
    );
  }

  // Error state
  if (error || !stagedImport) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <ErrorIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Failed to Load Import</h3>
            <p className="mt-2 text-sm text-gray-500">
              There was a problem loading the import data. Please try again.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Complete state
  if (currentStep === 'complete') {
    return (
      <div className="max-w-4xl mx-auto">
        <WizardHeader currentStep={currentStep} stagedImport={stagedImport} />
        <Card>
          <div className="text-center py-12">
            <SuccessIcon className="mx-auto h-16 w-16 text-green-500" />
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Import Complete!</h3>
            <p className="mt-2 text-gray-600">
              Your transactions have been successfully imported.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <Button variant="outline" onClick={() => navigate('/accounts')}>
                View Accounts
              </Button>
              <Button variant="primary" onClick={handleFinish}>
                View Transactions
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <WizardHeader currentStep={currentStep} stagedImport={stagedImport} />

      {currentStep === 'account_resolution' && (
        <AccountResolutionStep
          stagedImport={stagedImport}
          onComplete={handleAccountResolutionComplete}
          onCancel={handleCancel}
        />
      )}

      {currentStep === 'transaction_review' && (
        <TransactionReviewStep
          stagedImport={stagedImport}
          onComplete={handleTransactionReviewComplete}
          onBack={handleBack}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}

// ============================================================================
// Wizard Header Component
// ============================================================================

interface WizardHeaderProps {
  currentStep: WizardStep;
  stagedImport?: StagedImport | null;
}

/**
 * Determine if a step is complete based on staged import state (WP-BUG-13)
 */
function isStepComplete(step: WizardStep, stagedImport: StagedImport | null | undefined): boolean {
  if (!stagedImport) {
    return step === 'upload'; // Upload is complete if we have a staged import ID
  }

  switch (step) {
    case 'upload':
      return true; // Always complete if we have a staged import
    case 'account_resolution':
      return Boolean(stagedImport.confirmedAccountId);
    case 'transaction_review':
      return false; // Only complete after import
    case 'complete':
      return false;
    default:
      return false;
  }
}

function WizardHeader({ currentStep, stagedImport }: WizardHeaderProps) {
  const currentStepNumber = getStepNumber(currentStep);

  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-gray-900">Import Document</h1>
      <p className="mt-1 text-gray-600">
        Step {currentStepNumber} of {STEPS.length}
      </p>

      {/* Progress indicator */}
      <div className="mt-4">
        <div className="flex items-center">
          {STEPS.map((step, index) => {
            const isComplete = isStepComplete(step.key, stagedImport);
            const isCurrent = step.number === currentStepNumber;
            const isPast = step.number < currentStepNumber;
            // Show warning if we're past this step but it's not complete (WP-BUG-13)
            const needsAttention = isPast && !isComplete && step.key === 'account_resolution';

            return (
              <div key={step.key} className="flex items-center">
                {/* Step circle */}
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium ${
                    needsAttention
                      ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                      : isComplete
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : isCurrent
                          ? 'border-primary-600 text-primary-600'
                          : 'border-gray-300 text-gray-400'
                  }`}
                >
                  {needsAttention ? (
                    <WarningIcon className="w-5 h-5" />
                  ) : isComplete ? (
                    <CheckIcon className="w-5 h-5" />
                  ) : (
                    step.number
                  )}
                </div>

                {/* Step label */}
                <span
                  className={`ml-2 text-sm ${
                    needsAttention
                      ? 'text-yellow-700 font-medium'
                      : isCurrent || isPast
                        ? 'text-gray-900 font-medium'
                        : 'text-gray-400'
                  }`}
                >
                  {step.label}
                  {needsAttention && <span className="ml-1">(Required)</span>}
                </span>

                {/* Connector line */}
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 min-w-[40px] ${
                      isComplete ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function SuccessIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-6 w-6 text-primary-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// ============================================================================
// Orphaned Document Error Component
// ============================================================================

interface OrphanedDocumentErrorProps {
  documentId: string;
  documentStatus: DocumentStatus | null;
  onCancel: () => void;
  onRetry: () => void;
}

/**
 * Component shown when a document reaches a terminal status (staged, committed)
 * but has no staged_import_id. This indicates the document was processed before
 * a bug fix was deployed or only contains balance information (no transactions).
 */
function OrphanedDocumentError({
  documentId,
  documentStatus,
  onCancel,
  onRetry,
}: OrphanedDocumentErrorProps) {
  const retryMutation = useRetryDocument();
  const navigate = useNavigate();
  const [retryAttempted, setRetryAttempted] = useState(false);

  const handleReprocess = useCallback(async () => {
    try {
      setRetryAttempted(true);
      await retryMutation.mutateAsync(documentId);
      // Navigate to the same page with documentId to start polling again
      navigate(`/documents/import?documentId=${documentId}`, { replace: true });
      // Force a page reload to reset polling state
      window.location.reload();
    } catch {
      // Error will be shown via mutation state
    }
  }, [documentId, retryMutation, navigate]);

  // Determine the appropriate message based on document status
  const getMessage = () => {
    if (documentStatus === 'committed') {
      return 'This document was already imported, but no transaction data is available for review. The document may have only contained balance information.';
    }
    return 'This document was processed but no import data was created. This may happen with documents that only contain balance information or were processed before a system update.';
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Card>
        <div className="text-center py-12">
          <WarningIcon className="mx-auto h-12 w-12 text-yellow-500" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No Import Data Available
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
            {getMessage()}
          </p>

          {retryMutation.isError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">
                Failed to reprocess document. Please try uploading a new document instead.
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <Button variant="outline" onClick={onCancel}>
              Back to Documents
            </Button>
            {!retryAttempted && documentStatus !== 'committed' && (
              <Button
                variant="secondary"
                onClick={handleReprocess}
                disabled={retryMutation.isPending}
              >
                {retryMutation.isPending ? 'Reprocessing...' : 'Reprocess Document'}
              </Button>
            )}
            <Button variant="primary" onClick={onRetry}>
              Upload New Document
            </Button>
          </div>

          <p className="mt-4 text-xs text-gray-400">
            Document ID: {documentId}
          </p>
        </div>
      </Card>
    </div>
  );
}
